import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "./use-toast";
import {
  getBrowserTimezone,
  resolveProfileTimezone,
  shouldAutoDetectProfileTimezone,
} from "@/utils/timezones";
import {
  PREVIEW_BEHAVIOR_NAME,
  PREVIEW_COACH_NAME,
  PREVIEW_LEADER_NAME,
} from "@/data/leaderPreviewData";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";
import { DEFAULT_LEADERSHIP_LENS_ID, resolveLeadershipLens, type LeadershipLensId } from "@/lib/leadershipLens";
import { SOLO_MODE_EXPERIENCE_QUERY_KEY } from "@/hooks/useSoloModeExperienceState";
import {
  DEFAULT_TRANSCRIPT_DELIVERY_PREFERENCE,
  resolveTranscriptDeliveryPreference,
  type TranscriptDeliveryPreference,
} from "@/lib/transcriptDeliveryPreference";

interface UserSettings {
  id: string;
  email: string | null;
  fullName: string | null;
  avatarUrl: string | null;
  coachId: string | null;
  accountMode: string | null;
  leadershipLens: LeadershipLensId;
  leaderRole: string | null;
  teamSize: string | null;
  yearsAsLeader: string | null;
  orgType: string | null;
  timezone: string;
  morningSummaryTime: string;
  nudgeLeadMinutes: number;
  calendarConnected: boolean;
  calendarTokenExpiry: string | null;
  companionEnabled: boolean;
  // Notification preferences
  emailNudgesEnabled: boolean;
  desktopNudgesEnabled: boolean;
  desktopPermissionStatus: string | null;
  transcriptDeliveryPreference: TranscriptDeliveryPreference;
  transcriptDeliveryPreferenceRemembered: boolean;
  transcriptDeliveryPreferenceSetAt: string | null;
  smsNudgesEnabled: boolean;
  phoneNumber: string | null;
  phoneVerified: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  // New SMS fields
  phoneE164: string | null;
  phoneVerifiedAt: string | null;
  smsConsentAt: string | null;
  smsOptedOutAt: string | null;
  // Zoom integration
  zoomConnected: boolean;
  // LinkedIn integration
  linkedinConnected: boolean;
  linkedinProfileMetadata: Record<string, unknown>;
  outlookConnected: boolean;
  meetConnected: boolean;
  defaultGoogleMeetLink: string | null;
  defaultZoomMeetingLink: string | null;
  defaultTeamsMeetingLink: string | null;
  personalizationHeroDismissed: boolean;
  /** When true the server-side weekly digest email is suppressed for this coach. */
  weeklyDigestOptOut: boolean;
}

interface UpdateSettingsData {
  fullName?: string;
  leaderRole?: string | null;
  teamSize?: string | null;
  yearsAsLeader?: string | null;
  orgType?: string | null;
  timezone?: string;
  morningSummaryTime?: string;
  nudgeLeadMinutes?: number;
  emailNudgesEnabled?: boolean;
  desktopNudgesEnabled?: boolean;
  desktopPermissionStatus?: string;
  transcriptDeliveryPreference?: TranscriptDeliveryPreference;
  transcriptDeliveryPreferenceRemembered?: boolean;
  smsNudgesEnabled?: boolean;
  phoneNumber?: string;
  quietHoursEnabled?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  linkedinConnected?: boolean;
  linkedinProfileMetadata?: Record<string, unknown>;
  outlookConnected?: boolean;
  defaultGoogleMeetLink?: string | null;
  defaultZoomMeetingLink?: string | null;
  defaultTeamsMeetingLink?: string | null;
  personalizationHeroDismissed?: boolean;
  leadershipLens?: LeadershipLensId;
  weeklyDigestOptOut?: boolean;
}

async function saveLeaderOnboardingLeadershipLens(
  leaderId: string,
  leadershipLens: LeadershipLensId,
) {
  const { data: existingOnboarding, error: lookupError } = await supabase
    .from("leader_onboarding")
    .select("id")
    .eq("leader_id", leaderId)
    .maybeSingle();

  if (lookupError) throw lookupError;

  if (existingOnboarding?.id) {
    const { error } = await supabase
      .from("leader_onboarding")
      .update({ leadership_lens: leadershipLens })
      .eq("leader_id", leaderId);

    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from("leader_onboarding")
    .insert({
      leader_id: leaderId,
      leadership_lens: leadershipLens,
    });

  if (error) throw error;
}

export function useUserSettings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-settings", user?.id],
    queryFn: async (): Promise<UserSettings | null> => {
      if (!user) return null;
      if (user.id === PREVIEW_LEADER_USER_ID) {
        return {
          id: user.id,
          email: user.email || null,
          fullName: PREVIEW_LEADER_NAME,
          avatarUrl: null,
          coachId: "preview-coach-user",
          accountMode: "coach_led",
          leadershipLens: DEFAULT_LEADERSHIP_LENS_ID,
          leaderRole: "VP Product",
          teamSize: "25-50",
          yearsAsLeader: "5-10",
          orgType: "SaaS",
          timezone: "America/Los_Angeles",
          morningSummaryTime: "07:00",
          nudgeLeadMinutes: 10,
          calendarConnected: true,
          calendarTokenExpiry: "2026-12-31T00:00:00.000Z",
          companionEnabled: true,
          emailNudgesEnabled: true,
          desktopNudgesEnabled: true,
          desktopPermissionStatus: "granted",
          transcriptDeliveryPreference: DEFAULT_TRANSCRIPT_DELIVERY_PREFERENCE,
          transcriptDeliveryPreferenceRemembered: false,
          transcriptDeliveryPreferenceSetAt: null,
          smsNudgesEnabled: false,
          phoneNumber: null,
          phoneVerified: false,
          quietHoursEnabled: true,
          quietHoursStart: "21:00",
          quietHoursEnd: "07:00",
          phoneE164: null,
          phoneVerifiedAt: null,
          smsConsentAt: null,
          smsOptedOutAt: null,
          zoomConnected: true,
          linkedinConnected: false,
          linkedinProfileMetadata: {
            preview: true,
            coachName: PREVIEW_COACH_NAME,
            behavior: PREVIEW_BEHAVIOR_NAME,
          },
          outlookConnected: false,
          defaultGoogleMeetLink: null,
          defaultZoomMeetingLink: null,
          defaultTeamsMeetingLink: null,
          meetConnected: true,
          personalizationHeroDismissed: false,
          weeklyDigestOptOut: false,
        };
      }

      const { data: coachLink } = await supabase
        .from("coach_leader_links")
        .select("coach_id")
        .eq("leader_id", user.id)
        .maybeSingle();

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching settings:", error);
        return null;
      }

      const { data: onboarding } = await supabase
        .from("leader_onboarding")
        .select("leadership_lens")
        .eq("leader_id", user.id)
        .maybeSingle();

      const typedData = data as {
        phone_e164?: string | null;
        phone_verified_at?: string | null;
        sms_consent_at?: string | null;
        sms_opted_out_at?: string | null;
        leader_role?: string | null;
        team_size?: string | null;
        years_as_leader?: string | null;
        org_type?: string | null;
        zoom_connected?: boolean;
        linkedin_connected?: boolean;
        linkedin_profile_metadata?: Record<string, unknown>;
        outlook_connected?: boolean;
        default_google_meet_link?: string | null;
        default_zoom_meeting_link?: string | null;
        default_teams_meeting_link?: string | null;
        meet_connected?: boolean;
        personalization_hero_dismissed?: boolean;
        account_mode?: string | null;
        timezone_auto_detected_at?: string | null;
        timezone_manually_set_at?: string | null;
        transcript_delivery_preference?: string | null;
        transcript_delivery_preference_remembered?: boolean | null;
        transcript_delivery_preference_set_at?: string | null;
      } | null;

      const typedOnboarding = onboarding as { leadership_lens?: LeadershipLensId | null } | null;
      const browserTimezone = getBrowserTimezone();
      const resolvedTimezone = resolveProfileTimezone({
        profileTimezone: data?.timezone,
        timezoneManuallySetAt: typedData?.timezone_manually_set_at,
        browserTimezone,
      });

      if (shouldAutoDetectProfileTimezone({
        profileTimezone: data?.timezone,
        timezoneManuallySetAt: typedData?.timezone_manually_set_at,
        browserTimezone,
      })) {
        const { error: timezoneUpdateError } = await supabase
          .from("profiles")
          .update({
            timezone: resolvedTimezone,
            timezone_auto_detected_at: new Date().toISOString(),
          })
          .eq("id", user.id);

        if (timezoneUpdateError) {
          console.error("Error saving detected timezone:", timezoneUpdateError);
        }
      }

      return {
        id: data?.id || user.id,
        email: data?.email || user.email || null,
        fullName: data?.full_name || null,
        avatarUrl: data?.avatar_url || null,
        coachId: coachLink?.coach_id || data?.coach_id || null,
        accountMode: typedData?.account_mode || null,
        leadershipLens: resolveLeadershipLens(typedOnboarding?.leadership_lens).id,
        leaderRole: typedData?.leader_role || null,
        teamSize: typedData?.team_size || null,
        yearsAsLeader: typedData?.years_as_leader || null,
        orgType: typedData?.org_type || null,
        timezone: resolvedTimezone,
        morningSummaryTime: data?.morning_summary_time || "07:00",
        nudgeLeadMinutes: data?.nudge_lead_minutes || 10,
        calendarConnected: data?.calendar_connected || false,
        calendarTokenExpiry: data?.calendar_token_expiry || null,
        companionEnabled: data?.companion_enabled ?? true,
        // Notification preferences
        emailNudgesEnabled: data?.email_nudges_enabled ?? true,
        desktopNudgesEnabled: data?.desktop_nudges_enabled ?? false,
        desktopPermissionStatus: data?.desktop_permission_status || null,
        transcriptDeliveryPreference: resolveTranscriptDeliveryPreference(typedData?.transcript_delivery_preference),
        transcriptDeliveryPreferenceRemembered: typedData?.transcript_delivery_preference_remembered ?? false,
        transcriptDeliveryPreferenceSetAt: typedData?.transcript_delivery_preference_set_at || null,
        smsNudgesEnabled: data?.sms_nudges_enabled ?? false,
        phoneNumber: data?.phone_number || null,
        phoneVerified: data?.phone_verified ?? false,
        quietHoursEnabled: data?.quiet_hours_enabled ?? true,
        quietHoursStart: data?.quiet_hours_start || "21:00",
        quietHoursEnd: data?.quiet_hours_end || "07:00",
        // New SMS fields
        phoneE164: typedData?.phone_e164 || null,
        phoneVerifiedAt: typedData?.phone_verified_at || null,
        smsConsentAt: typedData?.sms_consent_at || null,
        smsOptedOutAt: typedData?.sms_opted_out_at || null,
        // Zoom integration
        zoomConnected: typedData?.zoom_connected || false,
        // LinkedIn integration
        linkedinConnected: typedData?.linkedin_connected || false,
        linkedinProfileMetadata: typedData?.linkedin_profile_metadata || {},
        outlookConnected: typedData?.outlook_connected || false,
        defaultGoogleMeetLink: typedData?.default_google_meet_link || null,
        defaultZoomMeetingLink: typedData?.default_zoom_meeting_link || null,
        defaultTeamsMeetingLink: typedData?.default_teams_meeting_link || null,
        meetConnected: typedData?.meet_connected || false,
        personalizationHeroDismissed: typedData?.personalization_hero_dismissed || false,
        weeklyDigestOptOut: (data as Record<string, unknown> | null)?.weekly_digest_opt_out === true,
      };
    },
    enabled: !!user,
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateSettingsData) => {
      if (!user) throw new Error("Not authenticated");

      const updateData: Record<string, unknown> = {};
      if (data.fullName !== undefined) updateData.full_name = data.fullName;
      if (data.leaderRole !== undefined) updateData.leader_role = data.leaderRole;
      if (data.teamSize !== undefined) updateData.team_size = data.teamSize;
      if (data.yearsAsLeader !== undefined) updateData.years_as_leader = data.yearsAsLeader;
      if (data.orgType !== undefined) updateData.org_type = data.orgType;
      if (data.timezone !== undefined) {
        updateData.timezone = data.timezone;
        updateData.timezone_manually_set_at = new Date().toISOString();
      }
      if (data.morningSummaryTime !== undefined) updateData.morning_summary_time = data.morningSummaryTime;
      if (data.nudgeLeadMinutes !== undefined) updateData.nudge_lead_minutes = data.nudgeLeadMinutes;
      if (data.emailNudgesEnabled !== undefined) updateData.email_nudges_enabled = data.emailNudgesEnabled;
      if (data.desktopNudgesEnabled !== undefined) updateData.desktop_nudges_enabled = data.desktopNudgesEnabled;
      if (data.desktopPermissionStatus !== undefined) updateData.desktop_permission_status = data.desktopPermissionStatus;
      if (data.transcriptDeliveryPreference !== undefined) {
        updateData.transcript_delivery_preference = data.transcriptDeliveryPreference;
        updateData.transcript_delivery_preference_set_at = new Date().toISOString();
      }
      if (data.transcriptDeliveryPreferenceRemembered !== undefined) {
        updateData.transcript_delivery_preference_remembered = data.transcriptDeliveryPreferenceRemembered;
        updateData.transcript_delivery_preference_set_at = new Date().toISOString();
      }
      if (data.smsNudgesEnabled !== undefined) updateData.sms_nudges_enabled = data.smsNudgesEnabled;
      if (data.phoneNumber !== undefined) updateData.phone_number = data.phoneNumber;
      if (data.quietHoursEnabled !== undefined) updateData.quiet_hours_enabled = data.quietHoursEnabled;
      if (data.quietHoursStart !== undefined) updateData.quiet_hours_start = data.quietHoursStart;
      if (data.quietHoursEnd !== undefined) updateData.quiet_hours_end = data.quietHoursEnd;
      if (data.linkedinConnected !== undefined) updateData.linkedin_connected = data.linkedinConnected;
      if (data.linkedinProfileMetadata !== undefined) updateData.linkedin_profile_metadata = data.linkedinProfileMetadata;
      if (data.outlookConnected !== undefined) updateData.outlook_connected = data.outlookConnected;
      if (data.defaultGoogleMeetLink !== undefined) updateData.default_google_meet_link = data.defaultGoogleMeetLink;
      if (data.defaultZoomMeetingLink !== undefined) updateData.default_zoom_meeting_link = data.defaultZoomMeetingLink;
      if (data.defaultTeamsMeetingLink !== undefined) updateData.default_teams_meeting_link = data.defaultTeamsMeetingLink;
      if (data.personalizationHeroDismissed !== undefined) updateData.personalization_hero_dismissed = data.personalizationHeroDismissed;
      if (data.weeklyDigestOptOut !== undefined) updateData.weekly_digest_opt_out = data.weeklyDigestOptOut;
      if (Object.keys(updateData).length > 0) {
        const profilePayload = {
          id: user.id,
          email: user.email ?? null,
          ...updateData,
        };
        const { error } = await supabase
          .from("profiles")
          .upsert(profilePayload, { onConflict: "id" });

        if (error) throw error;
      }

      if (data.leadershipLens !== undefined) {
        await saveLeaderOnboardingLeadershipLens(user.id, data.leadershipLens);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-settings"] });
      queryClient.invalidateQueries({ queryKey: [SOLO_MODE_EXPERIENCE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
      toast({
        title: "Settings saved",
        description: "Your preferences have been updated.",
      });
    },
    onError: (error) => {
      console.error("Error updating settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  const { signOut } = useAuth();

  return useMutation({
    mutationFn: async () => {
      // Stop any in-flight authenticated queries before we drop the session.
      await queryClient.cancelQueries();
      queryClient.clear();

      await signOut();
    },
  });
}
